// src/pages/HrProfilePage.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import InviteHrTeamDialog from '../components/InviteHrTeamDialog';
import { Button } from '@mui/material';
import HrTeamTable from '../components/HrTeamTable';
import { useTranslation } from 'react-i18next';
import { authApi } from '../axiosInstance';
import ensureAuthSessionUtil from '../utils/ensureAuthSession';

/* ============================== Helpers ============================== */

function readCookie(name) {
  const hit = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.split('=')[1]) : '';
}

async function getCsrfToken() {
  // NGINX injiziert X-CSRF-Token aus Cookie automatisch weiter.
  // Wir bieten hier nur eine defensive Fallback-Lösung an:
  return (
    readCookie('XSRF-TOKEN') ||
    readCookie('_csrf') ||
    readCookie('CSRF-TOKEN') ||
    readCookie('csrfToken') ||
    ''
  );
}

/* --- Mini-Toast für kurze Bestätigungen (Drop-in, keine Dependencies) --- */
function toast(msg, ok = true) {
  const el = document.createElement('div');
  el.textContent = msg;
  el.style.cssText = `
    position: fixed; right: 16px; bottom: 16px;
    background: ${ok ? '#16a34a' : '#dc2626'};
    color: #fff; padding: 10px 14px; border-radius: 10px;
    box-shadow: 0 10px 20px rgba(0,0,0,.15); z-index: 9999;
    font-weight: 600; letter-spacing: .2px;
  `;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2500);
}

/** /auth/whoami normalisieren */
function normAuthWho(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const u = raw.user ?? raw.profile ?? raw.data ?? raw;
  return {
    role: u.role ?? raw.role ?? '',
    // akzeptiere zusätzlich firstname/lastname
    firstName: u.firstName ?? u.first_name ?? u.firstname ?? '',
    lastName:  u.lastName  ?? u.last_name  ?? u.lastname  ?? '',
    email: u.email ?? '',
    // zusätzliche ClientId-Varianten tolerieren
    customerId: u.customer_id ?? u.customerId ?? u.client_id ?? u.clientId ?? null,
  };
}

/** /mentoring/whoami normalisieren */
function normMentoringWho(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const u = raw.user ?? raw;
  return {
    role: u.role ?? '',
    // akzeptiere zusätzlich firstname/lastname
    firstName: u.firstName ?? u.first_name ?? u.firstname ?? '',
    lastName:  u.lastName  ?? u.last_name  ?? u.lastname  ?? '',
    email: u.email ?? '',
    // zusätzliche Client/Customer-Varianten tolerieren
    customerId: u.customer_id ?? u.client_id ?? u.customerId ?? u.clientId ?? null,
  };
}

/** Firmendaten normalisieren */
function normalizeCompany(c) {
  if (!c || typeof c !== 'object') return {};
  const src = c.data ?? c;
  return {
    name: src.name ?? src.company_name ?? '',
    staffSize: src.staffSize ?? src.staff_size ?? '',
    revenue: src.revenue ?? src.turnover ?? src.annual_revenue ?? '',
    headquarters: src.headquarters ?? src.hq ?? '',
    locations: src.locations ?? src.sites ?? '',
    website: src.website ?? src.url ?? '',
    hasMentoringPrograms: src.hasMentoringPrograms ?? src.has_mentoring_programs ?? false,
    mainIndustry: src.mainIndustry ?? src.main_id ?? '',
    subIndustry: src.subIndustry ?? src.sub_id ?? '',
  };
}

/**
 * AUTH-Session sicherstellen, basierend auf dem gemeinsamen Utility.
 * Liefert weiterhin { authAvailable, user } wie vorher erwartet.
 */
async function ensureAuthSession() {
  // 1) Versuche AUTH-Session über das zentrale Utility aufzubauen
  try {
    const who = await ensureAuthSessionUtil(); // -> { isAuthenticated, user, role }
    const authed = (who?.isAuthenticated === true) || (who?.ok === true);
    if (authed && who.user) {
      return { authAvailable: true, user: normAuthWho(who) };
    }
  } catch {
    // stiller Fallback unten
  }

  // 1b) Direkter Fallback über /auth/whoami (falls Utility out-of-sync ist)
  try {
    const w = await fetch('/auth/whoami', { credentials: 'include' });
    if (w.ok) {
      const j = await w.json().catch(() => null);
      const authed = (j?.isAuthenticated === true) || (j?.ok === true);
      if (authed && j?.user) {
        return { authAvailable: true, user: normAuthWho(j) };
      }
    }
  } catch {
    // stiller Fallback unten
  }

  // 2) Read-Only Fallback: Mentoring-Identität verwenden, falls vorhanden
  try {
    const r = await fetch('/mentoring/whoami', { credentials: 'include' });
    if (r.ok) {
      const m = await r.json().catch(() => ({}));
      if (m?.user) {
        return { authAvailable: false, user: normMentoringWho(m) };
      }
    }
  } catch {
    // ignorieren → unten neutraler Rückgabewert
  }

  // 3) Kein nutzbarer Kontext
  return { authAvailable: false, user: {} };
}

/* ============================== Component ============================== */

export default function HrProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [authAvailable, setAuthAvailable] = useState(false);

  const [userRole, setUserRole] = useState('');
  const [customerId, setCustomerId] = useState(null);

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    department: '',
    jobTitle: '',
    email: '',
  });

  const [company, setCompany] = useState({
    name: '',
    staffSize: '',
    revenue: '',
    headquarters: '',
    locations: '',
    website: '',
    hasMentoringPrograms: false,
    mainIndustry: '',
    subIndustry: '',
  });

  const [mainIndustries, setMainIndustries] = useState([]);
  const [subIndustries, setSubIndustries] = useState([]);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // hr  oder hr_admin dürfen Firmendaten bearbeiten
  const isAdminOrHigher = userRole === 'hr' || userRole === 'hr_admin';
  const isCompanyEditable = authAvailable && isAdminOrHigher;

  const handleOpenInviteDialog = () => setShowInviteDialog(true);
  const handleCloseInviteDialog = () => setShowInviteDialog(false);

  /* ------------------------------ Initial laden ------------------------------ */
  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setError('');
      try {
        // Session-Setup (mentoring-first + auth-bridging)
        const sess = await ensureAuthSession();
        if (!mounted) return;

        setAuthAvailable(!!sess.authAvailable);

        // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        // Späte Re-Validierung: Falls beim ersten Versuch noch kein AUTH da war,
        // prüfe kurz darauf nochmal direkt /auth/whoami und aktiviere Schreibmodus.
        if (!sess.authAvailable) {
          setTimeout(async () => {
            try {
              const w = await fetch('/auth/whoami', { credentials: 'include' });
              if (!mounted) return;
              if (w.ok) {
                const j = await w.json();
                const authed = (j?.isAuthenticated === true) || (j?.ok === true);
                if (authed && j?.user) {
                  setAuthAvailable(true);
                  setUserRole(String(j.user.role || '').toLowerCase());
                  setCustomerId(j.user.customer_id ?? j.user.client_id ?? null);
                  setProfile(prev => ({
                    ...prev,
                    firstName: prev.firstName || j.user.firstName || j.user.first_name || j.user.firstname || '',
                    lastName:  prev.lastName  || j.user.lastName  || j.user.last_name  || j.user.lastname  || '',
                    email:     prev.email     || j.user.email     || ''
                  }));
                }
              }
            } catch { /* stiller Fallback */ }
          }, 600); // 0.6s ist in der Praxis ausreichend
        }
        // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

        const u = sess.user ?? {};
        const role = (u.role || '').toLowerCase();
        const validHrRoles = ['hr', 'hr_admin', 'hr_user'];

        if (!validHrRoles.includes(role)) {
          setError(t('hrProfile.noHrRole', 'Keine HR-Rolle. Zugriff verweigert.'));
          setLoading(false);
          return;
        }

        setUserRole(role);
        setCustomerId(u.customerId ?? null);

        // ► Profildaten laden (nur wenn AUTH verfügbar)
        if (sess.authAvailable) {
          try {
            const pr = await authApi.get('/profile', { withCredentials: true });
            const d   = pr?.data || {};
            const src = d.user || d; // <-- NEU: unterstützt whoami-Form und unsere /profile-Form

            setProfile(prev => ({
              ...prev,
              firstName: src.firstName ?? src.first_name ?? src.firstname ?? '',
              lastName:  src.lastName  ?? src.last_name  ?? src.lastname  ?? '',
              phone:     src.phone     ?? '',
              department:src.department?? '',
              jobTitle:  src.jobTitle  ?? src.job_title ?? '',
              email:     src.email     ?? prev.email ?? '',
            }));
          } catch (e) {
            // optional: console.warn('GET /auth/profile fehlgeschlagen', e);
          }
        }

        setProfile((prev) => ({
          ...prev,
          firstName: prev.firstName || u.firstName || u.first_name || u.firstname || '',
          lastName:  prev.lastName  || u.lastName  || u.last_name  || u.lastname  || '',
          email:     prev.email     || u.email     || '',
        }));

        // Firmen-Details nur laden, wenn AUTH-APIs verfügbar sind
        if (sess.authAvailable && u.customerId) {
          try {
            const resCompany = await authApi.get(`/api/customers/${u.customerId}`, {
              withCredentials: true,
            });
            setCompany((prev) => ({ ...prev, ...normalizeCompany(resCompany?.data) }));
          } catch {
            // tolerieren
          }

          try {
            const resMain = await authApi.get('/api/main-industries', {
              withCredentials: true,
            });
            setMainIndustries(resMain?.data || []);
          } catch {
            setMainIndustries([]);
          }
        } else {
          // Kein AUTH → keine Branchenlisten
          setMainIndustries([]);
          setSubIndustries([]);
        }
      } catch (e) {
        console.error('HR load error:', e);
        if (mounted) setError(t('hrProfile.loadingError', 'Fehler beim Laden.'));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------
  // Re-Fetch, sobald AUTH nachträglich verfügbar ist
  // -----------------------------------------------
  const didRefetchAfterAuth = useRef(false);

  useEffect(() => {
    if (!authAvailable || didRefetchAfterAuth.current) return;
    didRefetchAfterAuth.current = true;

    (async () => {
      try {
        // 1) User-Profildaten sicher nachladen
        const pr = await authApi.get('/profile', { withCredentials: true }).catch(() => null);
        const d  = pr?.data || {};
        const src = d.user || d; // /profile liefert bei uns { user }, aber tolerant bleiben

        setProfile(prev => ({
          ...prev,
          firstName:  src?.firstName ?? src?.first_name ?? src?.firstname ?? prev.firstName,
          lastName:   src?.lastName  ?? src?.last_name  ?? src?.lastname  ?? prev.lastName,
          phone:      src?.phone     ?? prev.phone,
          department: src?.department ?? prev.department,
          jobTitle:   src?.jobTitle  ?? src?.job_title ?? prev.jobTitle,
          email:      src?.email     ?? prev.email,
        }));

        // 2) Customer-ID sicher ermitteln (zur Not aus /auth/whoami)
        let cid = null;
        try {
          const who = await fetch('/auth/whoami', { credentials: 'include' }).then(r => r.json());
          cid = who?.user?.customer_id ?? who?.user?.client_id ?? null;
        } catch {}
        if (!cid) cid = customerId;
        if (cid && cid !== customerId) setCustomerId(cid);

        // 3) Firmendaten nachladen
        if (cid) {
          const resCompany = await authApi.get(`/api/customers/${cid}`, { withCredentials: true }).catch(() => null);
          if (resCompany?.data) {
            setCompany(prev => ({ ...prev, ...normalizeCompany(resCompany.data) }));
          }
        }

        // 4) Branchenlisten nachladen
        try {
          const resMain = await authApi.get('/api/main-industries', { withCredentials: true }).catch(() => null);
          setMainIndustries(resMain?.data || []);
        } catch {
          setMainIndustries([]);
        }

        // Subbranchen ggf. direkt hinterherschieben (wenn mainIndustry schon gesetzt ist)
        if (company.mainIndustry) {
          try {
            const resSub = await authApi.get(
              `/api/sub-industries?main_id=${encodeURIComponent(company.mainIndustry)}`,
              { withCredentials: true }
            ).catch(() => null);
            setSubIndustries(resSub?.data || []);
          } catch {
            setSubIndustries([]);
          }
        }
      } catch {
        // stiller Fallback – UI bleibt einfach bei bisherigen Werten
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAvailable]);

  /* ----------------------- Subbranchen dynamisch laden ----------------------- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!authAvailable) {
        setSubIndustries([]);
        return;
      }
      if (!company.mainIndustry) {
        setSubIndustries([]);
        return;
      }
      try {
        const res = await authApi.get(
          `/api/sub-industries?main_id=${encodeURIComponent(company.mainIndustry)}`,
          { withCredentials: true }
        );
        if (mounted) setSubIndustries(res?.data || []);
      } catch {
        if (mounted) setSubIndustries([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [authAvailable, company.mainIndustry]);

  /* ------------------------------- Handlers --------------------------------- */

  const handleMainIndustryChange = (e) => {
    if (!isCompanyEditable) return;
    const selectedMainId = e.target.value;
    setCompany((prev) => ({ ...prev, mainIndustry: selectedMainId, subIndustry: '' }));
  };

  const handleSubIndustryChange = (e) => {
    if (!isCompanyEditable) return;
    setCompany((prev) => ({ ...prev, subIndustry: e.target.value }));
  };

  /**
   * Speichern:
   * - Wenn AUTH verfügbar: immer /auth/profile PATCH (Userdaten)
   * - Falls Rolle hr|hr_admin und customerId vorhanden: zusätzlich /auth/api/customers/:id PATCH (Firmendaten)
   */
  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!authAvailable) {
      setError(
        t(
          'hrProfile.writeDisabled',
          'Schreibvorgänge sind aktuell nicht verfügbar (keine AUTH-Session).'
        )
      );
      return;
    }

    try {
      const csrf = await getCsrfToken();

      // 1) User-Profildaten (immer erlaubt, sobald AUTH da ist)
      try {
        await authApi.patch(
          '/profile',
          {
            firstName: profile.firstName,
            lastName: profile.lastName,
            phone: profile.phone,
            department: profile.department,
            jobTitle: profile.jobTitle,
          },
          {
            headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
            withCredentials: true,
          }
        );
      } catch (err) {
        console.warn('PATCH /auth/profile failed (toleriert wenn Server CSRF selbst injiziert):', err);
      }

      // 2) Firmendaten nur für hr/hr_admin
      if (isAdminOrHigher && customerId) {
        const payload = {
          name: company.name,
          staffSize: company.staffSize,
          revenue: company.revenue,
          headquarters: company.headquarters,
          locations: company.locations,
          website: company.website,
          hasMentoringPrograms: company.hasMentoringPrograms,
          mainIndustry: company.mainIndustry,
          subIndustry: company.subIndustry,
        };

        try {
          await authApi.patch(`/api/customers/${customerId}`, payload, {
            headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
            withCredentials: true,
          });
        } catch (errPatch) {
          console.error('PATCH /api/customers/:id failed', errPatch);
          setError(t('hrProfile.companySaveError', 'Fehler beim Speichern der Firmendaten.'));
          // Personendaten gelten trotzdem als gespeichert → kein return notwendig
        }
      }

      // ► Kurze Bestätigung
      toast(t('hrProfile.success', 'Erfolgreich gespeichert.'), true);
      setMessage(t('hrProfile.success', 'Erfolgreich gespeichert.'));
    } catch (err) {
      console.error('HR save error:', err);
      setError(t('hrProfile.error', 'Fehler beim Speichern der Daten'));
    }
  }

  /* -------------------------------- Render ---------------------------------- */

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white p-6">
        <p>{t('hrProfile.loadingUser', 'Lade Benutzer-Daten…')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white p-6">
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 bg-mentixxgreen text-black px-4 py-2 rounded"
        >
          {t('toHome', 'Zur Startseite')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate('/hr/overview')}
          className="bg-green-400 text-black font-semibold px-4 py-2 rounded-full hover:bg-green-300 transition"
        >
          {t('hrProfile.backToDashboard', '← Zum HR-Dashboard')}
        </button>

        {isAdminOrHigher && authAvailable && (
          <Button
            onClick={handleOpenInviteDialog}
            style={{
              float: 'right',
              backgroundColor: '#98FB98',
              color: 'black',
              fontWeight: 'bold',
              border: 'none',
            }}
          >
            {t('hrProfile.inviteHrTeam', 'HR-Teammitglied einladen')}
          </Button>
        )}
      </div>

      {/* Hinweis-Box komplett entfernt */}

      <h2 className="text-2xl font-bold mb-6">{t('hrProfile.heading', 'HR-Profil')}</h2>

      <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Userdaten */}
        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <h3 className="font-bold mb-4">{t('hrProfile.userData', 'User-Daten')}</h3>

          <label>{t('hrProfile.firstName', 'Vorname')}</label>
          <input
            type="text"
            value={profile.firstName}
            onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
          />

          <label>{t('hrProfile.lastName', 'Nachname')}</label>
          <input
            type="text"
            value={profile.lastName}
            onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
          />

          <label>{t('hrProfile.phone', 'Telefon')}</label>
          <input
            type="text"
            value={profile.phone}
            onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
          />

          <label>{t('hrProfile.department', 'Abteilung')}</label>
          <select
            value={profile.department}
            onChange={(e) => setProfile({ ...profile, department: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
          >
            <option value="">{t('hrProfile.select', 'Bitte auswählen')}</option>
            {Object.keys(t('hrProfile.fields', { returnObjects: true })).map((key) => (
              <option key={key} value={key}>
                {t(`hrProfile.fields.${key}`)}
              </option>
            ))}
          </select>

          <label>{t('hrProfile.jobTitle', 'Position')}</label>
          <input
            type="text"
            value={profile.jobTitle}
            onChange={(e) => setProfile({ ...profile, jobTitle: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
          />

          <label>{t('hrProfile.email', 'E-Mail')}</label>
          <input type="text" value={profile.email} readOnly className="w-full p-2 rounded bg-gray-700" />
        </div>

        {/* Firmendaten */}
        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <h3 className="font-bold mb-4">{t('hrProfile.companyData', 'Unternehmensdaten')}</h3>

          <label>{t('hrProfile.companyName', 'Firmenname')}</label>
          <input
            type="text"
            value={company.name}
            onChange={(e) => setCompany({ ...company, name: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
            readOnly={!isCompanyEditable}
          />

          <label>{t('hrProfile.staffSize', 'Mitarbeiteranzahl')}</label>
          <select
            value={company.staffSize}
            onChange={(e) => setCompany({ ...company, staffSize: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
            disabled={!isCompanyEditable}
          >
            <option value="">{t('hrProfile.select', 'Bitte auswählen')}</option>
            {Object.keys(t('hrProfile.staffSizeOptions', { returnObjects: true })).map((key) => (
              <option key={key} value={key}>
                {t(`hrProfile.staffSizeOptions.${key}`)}
              </option>
            ))}
          </select>

          <label>{t('hrProfile.revenue', 'Umsatz')}</label>
          <select
            value={company.revenue}
            onChange={(e) => setCompany({ ...company, revenue: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
            disabled={!isCompanyEditable}
          >
            <option value="">{t('hrProfile.select', 'Bitte auswählen')}</option>
            {Object.keys(t('hrProfile.revenueOptions', { returnObjects: true })).map((key) => (
              <option key={key} value={key}>
                {t(`hrProfile.revenueOptions.${key}`)}
              </option>
            ))}
          </select>

          <label>{t('hrProfile.headquarters', 'Hauptsitz')}</label>
          <input
            type="text"
            value={company.headquarters}
            onChange={(e) => setCompany({ ...company, headquarters: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
            readOnly={!isCompanyEditable}
          />

          <label>{t('hrProfile.locations', 'Standorte')}</label>
          <input
            type="text"
            value={company.locations}
            onChange={(e) => setCompany({ ...company, locations: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
            readOnly={!isCompanyEditable}
          />

          <label>{t('hrProfile.website', 'Website')}</label>
          <input
            type="text"
            value={company.website}
            onChange={(e) => setCompany({ ...company, website: e.target.value })}
            className="w-full p-2 rounded bg-gray-700 mb-2"
            readOnly={!isCompanyEditable}
          />

          <label className="inline-flex items-center gap-2 mt-2">
            <input
              type="checkbox"
              checked={company.hasMentoringPrograms}
              onChange={(e) =>
                setCompany({ ...company, hasMentoringPrograms: e.target.checked })
              }
              disabled={!isCompanyEditable}
            />
            <span>{t('hrProfile.hasMentoringPrograms', 'Hat Mentoring-Programme')}</span>
          </label>
        </div>

        {/* Branchen */}
        <div className="bg-gray-800 p-4 rounded-2xl shadow md:col-span-2">
          <label>{t('hrProfile.mainIndustry', 'Hauptbranche')}</label>
          <select
            value={company.mainIndustry}
            onChange={handleMainIndustryChange}
            className="w-full p-2 rounded bg-gray-700"
            disabled={!isCompanyEditable}
          >
            <option value="">{t('hrProfile.select', 'Bitte auswählen')}</option>
            {mainIndustries.map((mi) => (
              <option key={mi.main_id} value={mi.main_id}>
                {mi.main_name}
              </option>
            ))}
          </select>

          <label className="mt-4 block">{t('hrProfile.subIndustry', 'Unterbranche')}</label>
          <select
            value={company.subIndustry}
            onChange={handleSubIndustryChange}
            disabled={!isCompanyEditable || !company.mainIndustry}
            className="w-full p-2 rounded bg-gray-700 mt-2"
          >
            <option value="">{t('hrProfile.select', 'Bitte auswählen')}</option>
            {subIndustries.map((si) => (
              <option key={si.sub_id} value={si.sub_id}>
                {si.sub_name}
              </option>
            ))}
          </select>
        </div>

        {/* Aktionen */}
        <div className="md:col-span-2 flex flex-col items-center gap-4 mt-6">
          <button
            type="submit"
            className={`w-full md:w-1/2 py-2 rounded-2xl font-bold transition ${
              authAvailable
                ? 'bg-mentixxgreen text-black hover:bg-green-400'
                : 'bg-gray-700 text-gray-300 cursor-not-allowed'
            }`}
            disabled={!authAvailable}
          >
            {t('hrProfile.save', 'Speichern')}
          </button>

          <button
            type="button"
            onClick={() => navigate('/change-password')}
            className="bg-gray-700 w-full md:w-1/2 py-2 rounded-2xl text-white font-bold hover:bg-gray-600 transition"
          >
            {t('hrProfile.changePassword', 'Passwort ändern')}
          </button>
        </div>

        {/* HR-Team (nur bei AUTH + hr|hr_admin) */}
        {authAvailable && isAdminOrHigher && <HrTeamTable />}
      </form>

      {showInviteDialog && authAvailable && isAdminOrHigher && (
        <InviteHrTeamDialog
          onClose={handleCloseInviteDialog}
          onSuccess={() => alert(t('hrProfile.inviteSuccess', 'Einladung versendet.'))}
        />
      )}
    </div>
  );
}
